import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Bot,
  KeyRound,
  LayoutDashboard,
  RefreshCw,
  ShieldCheck,
  X,
} from 'lucide-react';
import { usePDFWorkflow } from '../hooks/usePDFWorkflow';
import { ErrorAlert } from '../shared/ErrorAlert';
import {
  WorkspaceBackButton,
  WorkspaceChecklist,
  WorkspacePage,
  WorkspaceSidebar,
  WorkspaceStageCard,
} from '../shared/WorkspaceChrome';
import { PDFUploadStep } from './PDFUploadStep';
import { ExcelTestCaseImportStep } from './ExcelTestCaseImportStep';
import { SectionExtractionStep } from './SectionExtractionStep';
import { SectionSelectionStep } from './SectionSelectionStep';
import { TestCasePlanningStep } from './TestCasePlanningStep';
import { GenerationProgressStep } from './GenerationProgressStep';
import { ResultsStep } from './ResultsStep';
import { EditTestCasesStep } from './EditTestCasesStep';
import { GeneratePOMStep } from './GeneratePOMStep';
import { MobilePOMStep } from './MobilePOMStep';
import { SaveTestsStep } from './SaveTestsStep';
import { GenerateScriptsStep } from './GenerateScriptsStep';
import { MobileAutoTestStep } from './MobileAutoTestStep';
import { ExecutionSummaryStep } from './ExecutionSummaryStep';
import { PreviousPDFsModal } from '../modals/PreviousPDFsModal';
import { WorkspaceMembersModal } from '../modals/WorkspaceMembersModal';
import { AutomationEnvironmentModal } from '../modals/AutomationEnvironmentModal';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';
import { loadJobSnapshot } from '../../services/jobs';
import { getWorkspaceVariantForIntake } from '../../config/workspaceVariants';
import {
  consumeWorkspaceBackRoute,
  wasCurrentDocumentReloaded,
} from '../../utils/workspaceNavigationHistory';
import {
  buildPdfWorkflowPath,
  normalizePdfWorkflowPath,
  normalizePdfWorkflowStep,
} from './workflowRoutes';
import {
  buildApplicationModulePath,
  buildApplicationPomEditorPath,
  buildApplicationPomsPath,
} from '../../utils/applicationRoutes';

const WORKFLOW_PHASES = [
  {
    label: 'Intake',
    description: 'Upload requirements and prepare source context',
    steps: [1, 1.5, 2, 3],
  },
  {
    label: 'Planning',
    description: 'Approve coverage mix before generation starts',
    steps: [3.5],
  },
  {
    label: 'Authoring',
    description: 'Generate, edit, and finalize test scenarios',
    steps: [4, 5, 6],
  },
  {
    label: 'Automation',
    description: 'Produce POM and generate scripts',
    steps: [5.5, 5.6, 7, 7.5],
  },
  {
    label: 'Delivery',
    description: 'Review execution outcomes and export reports',
    steps: [8],
  },
];

const getCurrentPhaseIndex = (currentStep) => {
  const index = WORKFLOW_PHASES.findIndex((phase) =>
    phase.steps.includes(currentStep)
  );
  return index === -1 ? 0 : index;
};

const formatModeLabel = () => 'BRD only';

const HISTORY_ROUTE_SOURCE_KEY = 'source';
const HISTORY_ROUTE_SOURCE_VALUE = 'history';

const isHistorySourceRoute = (search = '') => {
  const params = new URLSearchParams(search || '');
  return String(params.get(HISTORY_ROUTE_SOURCE_KEY) || '').trim().toLowerCase() === HISTORY_ROUTE_SOURCE_VALUE;
};

const buildHistorySourceSearch = () => `?${HISTORY_ROUTE_SOURCE_KEY}=${HISTORY_ROUTE_SOURCE_VALUE}`;

const getStepChecklist = (step) => {
  switch (step) {
    case 1:
      return {
        title: 'Intake Checklist',
        items: [
          {
            title: 'Use current requirements',
            description: 'Upload the latest approved BRD to avoid outdated scenarios.',
          },
          {
            title: 'Prefer searchable text',
            description: 'Select searchable PDFs or Word documents when possible for cleaner section extraction.',
          },
          {
            title: 'Reuse prior runs',
            description: 'Open a previous BRD when iterating on test planning and generation.',
          },
        ],
      };
    case 2:
      return {
        title: 'Extraction Checklist',
        items: [
          {
            title: 'Validate source document',
            description: 'Confirm the selected BRD is the correct baseline for extraction.',
          },
          {
            title: 'Complete one clean pass',
            description: 'Trigger extraction once and wait for completion before retrying.',
          },
        ],
      };
    case 1.5:
      return {
        title: 'Excel Mapping Checklist',
        items: [
          {
            title: 'Confirm required fields',
            description: 'Map the description, ordered steps, and expected result for every selected sheet.',
          },
          {
            title: 'Review sensitive data',
            description: 'Keep credentials and environment values as protected runtime inputs.',
          },
          {
            title: 'Check the normalized preview',
            description: 'Confirm step grouping and testcase intent before creating the source.',
          },
        ],
      };
    case 3:
      return {
        title: 'Section Selection Checklist',
        items: [
          {
            title: 'Focus on one target area',
            description: 'Select the section that best represents the current delivery goal.',
          },
          {
            title: 'Check section relevance',
            description: 'Confirm the section includes the business rules you want to validate.',
          },
          {
            title: 'Proceed with clear scope',
            description: 'Process only after finalizing the section to avoid noisy generation.',
          },
        ],
      };
    case 3.5:
      return {
        title: 'Planning Checklist',
        items: [
          {
            title: 'Balance coverage mix',
            description: 'Set positive and negative counts to match your test objectives.',
          },
          {
            title: 'Add focused intents',
            description: 'Include additional focus areas for edge conditions or key workflows.',
          },
          {
            title: 'Approve before generate',
            description: 'Confirm the plan now to reduce rework in the authoring stage.',
          },
        ],
      };
    case 4:
      return {
        title: 'Review Checklist',
        items: [
          {
            title: 'Check scenario quality',
            description: 'Review generated cases for clarity, relevance, and expected outcomes.',
          },
          {
            title: 'Spot coverage gaps',
            description: 'Identify missing business paths or risk-heavy conditions.',
          },
          {
            title: 'Regenerate selectively',
            description: 'Use additional generation only for uncovered flows.',
          },
        ],
      };
    case 5:
      return {
        title: 'Editing Checklist',
        items: [
          {
            title: 'Refine steps and data',
            description: 'Make each case actionable with concrete inputs and expected results.',
          },
          {
            title: 'Remove duplicates',
            description: 'Merge or drop overlapping scenarios to keep execution lean.',
          },
          {
            title: 'Confirm test intent',
            description: 'Ensure each case validates one clear business behavior.',
          },
        ],
      };
    case 5.5:
    case 5.6:
      return {
        title: 'POM Checklist',
        items: [
          {
            title: 'Use stable element strategy',
            description: 'Prefer robust selectors that survive minor UI changes.',
          },
          {
            title: 'Design reusable methods',
            description: 'Keep page actions generic enough for multiple test paths.',
          },
          {
            title: 'Validate naming consistency',
            description: 'Use clear naming for pages and actions to simplify maintenance.',
          },
        ],
      };
    case 6:
      return {
        title: 'Review & Approval Checklist',
        items: [
          {
            title: 'Review generated output',
            description: 'Validate the saved test cases for accuracy and completeness.',
          },
          {
            title: 'Share before approval',
            description: 'Download or email the workbook to stakeholders for review.',
          },
          {
            title: 'Request and track approval',
            description: 'Request Lead review and proceed to POM only after approval.',
          },
        ],
      };
    case 7:
    case 7.5:
      return {
        title: 'Script Generation Checklist',
        items: [
          {
            title: 'Generate with approved inputs',
            description: 'Use finalized test data and POM outputs for script generation.',
          },
          {
            title: 'Review generated artifacts',
            description: 'Inspect script structure and naming for readability and traceability.',
          },
          {
            title: 'Track execution outcomes',
            description: 'Capture failures with context for quick iteration.',
          },
        ],
      };
    case 8:
      return {
        title: 'Delivery Checklist',
        items: [
          {
            title: 'Review execution summary',
            description: 'Confirm pass/fail distribution and investigate unstable paths.',
          },
          {
            title: 'Export required outputs',
            description: 'Download reports needed for team review and audit.',
          },
          {
            title: 'Capture next actions',
            description: 'Document follow-ups for defects, coverage gaps, or reruns.',
          },
        ],
      };
    default:
      return {
        title: 'Step Checklist',
        items: [
          {
            title: 'Complete current step',
            description: 'Finish the action in this stage before moving ahead.',
          },
          {
            title: 'Verify output quality',
            description: 'Check results for correctness before continuing.',
          },
          {
            title: 'Proceed to next stage',
            description: 'Advance only after confirming readiness.',
          },
        ],
      };
  }
};

const getPreviousWorkflowStep = (currentStep) => {
  const backStepByCurrent = {
    1.5: 1,
    2: 1,
    3: 2,
    3.5: 3,
    4: 3,
    5: 3,
    5.5: 6,
    5.6: 6,
    6: 5,
    7: 5.5,
    7.5: 5.6,
    8: 7,
  };
  return backStepByCurrent[currentStep] ?? null;
};

const buildSectionReviewReturnSearch = (target) => {
  if (target?.type !== 'bulk_generation' || !String(target.bulkJobId || '').trim()) {
    return '';
  }
  const params = new URLSearchParams({
    return_to: 'bulk_generation',
    bulk_job_id: String(target.bulkJobId).trim(),
  });
  return `?${params.toString()}`;
};

const parseSectionReviewReturnTarget = (search, pdfId) => {
  const params = new URLSearchParams(search || '');
  const returnTo = String(params.get('return_to') || params.get('returnTo') || '').trim();
  const bulkJobId = String(params.get('bulk_job_id') || params.get('bulkJobId') || '').trim();
  if (returnTo !== 'bulk_generation' || !bulkJobId) {
    return null;
  }
  return {
    type: 'bulk_generation',
    pdfId: String(pdfId || '').trim(),
    bulkJobId,
  };
};

const isSameSectionReviewReturnTarget = (left, right) => (
  String(left?.type || '') === String(right?.type || '')
  && String(left?.pdfId || '') === String(right?.pdfId || '')
  && String(left?.bulkJobId || '') === String(right?.bulkJobId || '')
);

const routeSkeletonThemes = {
  brd: {
    page: 'workspace-product-brd',
    sidebar: 'workspace-hero-pdf',
  },
  requirements: {
    page: 'workspace-product-requirements',
    sidebar: 'workspace-hero-requirements',
  },
  api: {
    page: 'workspace-product-api',
    sidebar: 'workspace-hero-api',
  },
  excel: {
    page: 'workspace-product-excel',
    sidebar: 'workspace-hero-excel',
  },
};

const PDFRouteHydrationSkeleton = ({ theme: themeName = 'brd' }) => {
  const theme = routeSkeletonThemes[themeName] || routeSkeletonThemes.brd;
  return (
  <div className={`page-shell workspace-page-shell workspace-product-page ${theme.page}`}>
    <div className="mx-auto flex w-full max-w-[1680px] flex-col gap-6 2xl:max-w-[1720px]">
      <section className="workspace-grid workspace-grid-sidebar-hero workspace-unified-grid">
        <aside className={`workspace-sidebar-hero workspace-sidebar-standard ${theme.sidebar}`}>
          <div className="workspace-sidebar-grid" aria-hidden="true" />
          <div className="workspace-sidebar-orb" aria-hidden="true" />
          <div className="relative z-[1] animate-pulse">
            <div className="h-7 w-36 rounded-full bg-white/20" />
            <div className="mt-7 space-y-3">
              <div className="h-8 w-4/5 rounded bg-white/25" />
              <div className="h-8 w-2/3 rounded bg-white/25" />
              <div className="h-8 w-3/4 rounded bg-white/25" />
            </div>
            <div className="mt-7 space-y-2">
              <div className="h-4 w-full rounded bg-white/20" />
              <div className="h-4 w-4/5 rounded bg-white/20" />
            </div>
            <div className="mt-8 h-12 rounded-xl border border-white/25 bg-white/15" />
          </div>

          <div className="workspace-sidebar-metrics">
            {[0, 1, 2, 3].map((item) => (
              <div key={`route-sidebar-skeleton-${item}`} className="workspace-metric-card">
                <div className="animate-pulse">
                  <div className="h-3 w-28 rounded bg-white/20" />
                  <div className="mt-3 h-4 w-3/5 rounded bg-white/30" />
                </div>
              </div>
            ))}
          </div>
        </aside>

        <main className="workspace-main-panel">
          <div className="workspace-stage-card workspace-stage-card-standard-height">
            <div className="workspace-stage-header">
              <div className="animate-pulse">
                <div className="h-3 w-32 rounded bg-slate-200" />
                <div className="mt-3 h-7 w-48 rounded bg-slate-200" />
                <div className="mt-3 h-4 w-72 max-w-full rounded bg-slate-100" />
              </div>
              <div className="h-7 w-40 animate-pulse rounded-full bg-cyan-50" />
            </div>

            <div className="workspace-stage-body workspace-stage-body-checklisted">
              <div className="workspace-stage-content space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="animate-pulse space-y-4">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="space-y-3">
                        <div className="h-3 w-28 rounded bg-blue-100" />
                        <div className="h-7 w-64 max-w-full rounded bg-slate-200" />
                        <div className="h-4 w-80 max-w-full rounded bg-slate-100" />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <div className="h-10 w-28 rounded-xl bg-slate-100" />
                        <div className="h-10 w-32 rounded-xl bg-slate-100" />
                      </div>
                    </div>
                    <div className="grid gap-3 md:grid-cols-3">
                      {[0, 1, 2].map((card) => (
                        <div
                          key={`route-card-skeleton-${card}`}
                          className="rounded-2xl border border-slate-200 p-4"
                        >
                          <div className="h-10 w-10 rounded-xl bg-slate-100" />
                          <div className="mt-4 h-4 w-3/5 rounded bg-slate-200" />
                          <div className="mt-3 h-3 w-full rounded bg-slate-100" />
                          <div className="mt-2 h-3 w-4/5 rounded bg-slate-100" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
                    <div className="h-4 w-48 animate-pulse rounded bg-slate-200" />
                    <div className="h-7 w-24 animate-pulse rounded-full bg-slate-100" />
                  </div>
                  <div className="divide-y divide-slate-100">
                    {[0, 1, 2].map((row) => (
                      <div key={`route-row-skeleton-${row}`} className="px-4 py-4">
                        <div className="animate-pulse space-y-3">
                          <div className="h-4 w-3/4 rounded bg-slate-200" />
                          <div className="h-3 w-1/2 rounded bg-slate-100" />
                          <div className="grid gap-3 sm:grid-cols-3">
                            <div className="h-8 rounded-md bg-blue-50" />
                            <div className="h-8 rounded-md bg-indigo-50" />
                            <div className="h-8 rounded-md bg-slate-100" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </div>
          </div>
        </main>
      </section>
    </div>
  </div>
  );
};

export const PDFWorkflow = ({
  currentUser = null,
  intakeMode = 'document',
  applicationId = '',
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isTextRequirementsWorkflow = intakeMode === 'text';
  const isApiTestingWorkflow = intakeMode === 'api';
  const isExcelTestCasesWorkflow = intakeMode === 'excel';
  const workspaceVariant = getWorkspaceVariantForIntake(intakeMode);
  const normalizedApplicationId = String(applicationId || '').trim();
  const applicationRouteMode = Boolean(normalizedApplicationId);
  const workflow = usePDFWorkflow(currentUser, {
    intakeMode,
    applicationId: normalizedApplicationId,
  });
  // Runtime source detection (a reloaded job can be Excel/OpenAPI-sourced
  // regardless of which intake the workspace was mounted with) takes
  // priority over the static intakeMode, matching the testingTypeMode check
  // further down.
  const scriptStageTheme = workflow.isOpenApiSource
    ? 'api'
    : workflow.isExcelSource
      ? 'excel'
      : workspaceVariant.theme;
  const routeRoot = isTextRequirementsWorkflow
    ? 'requirements'
    : isApiTestingWorkflow
      ? 'api-testing'
      : isExcelTestCasesWorkflow
        ? 'excel-testcases'
        : 'pdf';
  const applicationModule = isTextRequirementsWorkflow
    ? 'requirements'
    : isApiTestingWorkflow
      ? 'api'
      : isExcelTestCasesWorkflow
        ? 'excel'
        : 'brd';
  const moduleDashboardPath = applicationRouteMode
    ? buildApplicationModulePath(normalizedApplicationId, applicationModule)
    : `/${routeRoot}`;
  const workflowRef = React.useRef(workflow);
  const deepLinkHandledRef = React.useRef('');
  const pathLinkHandledRef = React.useRef('');
  const generationRestoreAttemptRef = React.useRef('');
  const activeBulkJob = workflow.bulkJob;
  const activeBulkLoading = workflow.bulkLoading;
  const restoreBulkGenerationById = workflow.restoreBulkGenerationById;
  const restoreLatestBulkGeneration = workflow.restoreLatestBulkGeneration;
  const routeHydrationRef = React.useRef(false);
  const routeHydratingSignatureRef = React.useRef('');
  const routeHydrationTokenRef = React.useRef(0);
  const replaceNextRouteSyncRef = React.useRef(false);
  const pomInternalBackRef = React.useRef(null);
  const [routeHydratingSignature, setRouteHydratingSignature] = React.useState('');
  const [routeHydrationFailedSignature, setRouteHydrationFailedSignature] = React.useState('');
  const [pomHasInternalBack, setPomHasInternalBack] = React.useState(false);
  const [sectionReviewReturnTarget, setSectionReviewReturnTarget] = React.useState(null);
  const [showAutomationLoginPrompt, setShowAutomationLoginPrompt] = React.useState(false);
  const [showAutomationCaptureModal, setShowAutomationCaptureModal] = React.useState(false);
  const [automationAuthChecking, setAutomationAuthChecking] = React.useState(false);
  const [applicationEnvironment, setApplicationEnvironment] = React.useState(null);
  const currentPhaseIndex = getCurrentPhaseIndex(workflow.currentStep);
  React.useEffect(() => {
    if (!applicationRouteMode) {
      setApplicationEnvironment(null);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const response = await apiFetch(
          `${API_BASE_URL}${API_ENDPOINTS.APPLICATION_ENVIRONMENTS(normalizedApplicationId)}`
        );
        if (!response.ok) return;
        const payload = await response.json().catch(() => ({}));
        const environments = Array.isArray(payload?.environments) ? payload.environments : [];
        const defaultEnvironment = environments.find((item) => item?.is_default) || environments[0] || null;
        if (!cancelled) setApplicationEnvironment(defaultEnvironment);
      } catch (_err) {
        if (!cancelled) setApplicationEnvironment(null);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [applicationRouteMode, normalizedApplicationId]);
  const automationAuthSourceId = String(
    applicationEnvironment?.auth_source_id || workflow.pdfId || ''
  ).trim();
  const baseCurrentPhase = WORKFLOW_PHASES[currentPhaseIndex];
  const currentPhase = currentPhaseIndex === 0
    ? { ...baseCurrentPhase, description: workspaceVariant.intakeDescription }
    : baseCurrentPhase;
  const currentChecklist = workflow.currentStep === 1
    ? workspaceVariant.intakeChecklist
    : getStepChecklist(workflow.currentStep);
  const currentGlobalRole = String(currentUser?.role || '').trim().toLowerCase();
  const currentWorkspaceRole = String(workflow.selectedWorkspace?.current_user_role || '').trim().toLowerCase();
  const canDownloadExtractedSectionsJson = currentGlobalRole === 'admin' || currentWorkspaceRole === 'admin';
  const workflowSourceConfig = React.useMemo(() => {
    const sourceTypes = new Set([
      String(workflow.selectedPreviousPdf?.source_type || '').trim().toLowerCase(),
      String(workflow.selectedPreviousPdf?.item_type || '').trim().toLowerCase(),
      String(workflow.processingResult?.source_type || '').trim().toLowerCase(),
      String(workflow.processingResult?.item_type || '').trim().toLowerCase(),
      ...(Array.isArray(workflow.sections)
        ? workflow.sections.map((section) => String(section?.source_type || '').trim().toLowerCase())
        : []),
    ].filter(Boolean));

    const hasExcelSource = workflow.isExcelSource
      || isExcelTestCasesWorkflow
      || ['testcase_excel', 'test_case_excel', 'excel_test_cases'].some((type) => sourceTypes.has(type));
    if (hasExcelSource) {
      return {
        label: 'Excel workbook',
        sourceType: 'testcase_excel',
        contextType: 'source_section',
      };
    }

    const hasOpenApiSource = workflow.isOpenApiSource
      || isApiTestingWorkflow
      || sourceTypes.has('openapi')
      || sourceTypes.has('openapi_operation');
    if (hasOpenApiSource) {
      return {
        label: 'OpenAPI operation',
        sourceType: 'openapi',
        contextType: 'source_section',
      };
    }

    const hasRequirementSource = isTextRequirementsWorkflow
      || sourceTypes.has('manual_input')
      || sourceTypes.has('manual_upload');
    if (hasRequirementSource) {
      return {
        label: 'Requirement source',
        sourceType: 'manual_input',
        contextType: 'source_section',
      };
    }

    return {
      label: 'BRD',
      sourceType: sourceTypes.has('word_document') ? 'word_document' : 'brd',
      contextType: 'source_section',
    };
  }, [
    isApiTestingWorkflow,
    isExcelTestCasesWorkflow,
    isTextRequirementsWorkflow,
    workflow.isExcelSource,
    workflow.isOpenApiSource,
    workflow.processingResult,
    workflow.sections,
    workflow.selectedPreviousPdf,
  ]);
  React.useEffect(() => {
    workflowRef.current = workflow;
  }, [workflow]);

  const getTestCasesForEditing = (testCases) => {
    if (!testCases) return [];
    if (Array.isArray(testCases)) return testCases;
    if (
      typeof testCases === 'object' &&
      ('positive' in testCases || 'negative' in testCases)
    ) {
      return {
        positive: Array.isArray(testCases.positive) ? testCases.positive : [],
        negative: Array.isArray(testCases.negative) ? testCases.negative : [],
      };
    }
    return [testCases];
  };

  const hasAnyTestCases = (testCases) => {
    if (!testCases) return false;
    if (Array.isArray(testCases)) return testCases.length > 0;
    if (typeof testCases === 'object' && ('positive' in testCases || 'negative' in testCases)) {
      const positiveCount = Array.isArray(testCases.positive) ? testCases.positive.length : 0;
      const negativeCount = Array.isArray(testCases.negative) ? testCases.negative.length : 0;
      return positiveCount + negativeCount > 0;
    }
    return true;
  };

  const beginRouteHydration = React.useCallback((signature) => {
    const token = routeHydrationTokenRef.current + 1;
    routeHydrationTokenRef.current = token;
    routeHydrationRef.current = true;
    routeHydratingSignatureRef.current = signature;
    setRouteHydratingSignature(signature);
    setRouteHydrationFailedSignature('');
    return token;
  }, []);

  const finishRouteHydration = React.useCallback((signature, token) => {
    if (
      routeHydratingSignatureRef.current !== signature
      || routeHydrationTokenRef.current !== token
    ) return;
    routeHydratingSignatureRef.current = '';
    routeHydrationRef.current = false;
    setRouteHydratingSignature('');
  }, []);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const targetPdfId = String(params.get('pdf_id') || '').trim();
    const targetSectionNumber = Number(params.get('section'));
    const targetStep = Number(params.get('step'));
    const targetBulkJobId = String(params.get('bulk_job_id') || params.get('bulkJobId') || '').trim();
    const openedFromHistory = isHistorySourceRoute(location.search);
    const normalizedTargetStep = Number.isFinite(targetStep)
      ? normalizePdfWorkflowStep(targetStep, 5)
      : null;

    if (!targetPdfId) {
      return undefined;
    }

    const hasSectionTarget = Number.isFinite(targetSectionNumber) && targetSectionNumber > 0;
    const signature = `${targetPdfId}:${hasSectionTarget ? targetSectionNumber : 'none'}:${targetBulkJobId || 'none'}:${Number.isFinite(targetStep) ? targetStep : 'default'}:${openedFromHistory ? 'history' : 'direct'}`;
    if (deepLinkHandledRef.current === signature) {
      return undefined;
    }
    deepLinkHandledRef.current = signature;
    const hydrationSignature = `query:${signature}`;
    const hydrationToken = beginRouteHydration(hydrationSignature);

    let cancelled = false;
    let finished = false;
    const run = async () => {
      const activeWorkflow = workflowRef.current;
      try {
        await activeWorkflow.hydratePdfWorkflow({
          pdf: {
            pdf_id: targetPdfId,
            pdf_file: targetPdfId,
          },
          sectionNumber: hasSectionTarget ? targetSectionNumber : null,
          targetStep: normalizedTargetStep,
          markLoadedFromHistory: openedFromHistory,
        });
        if (cancelled) return;

        const allowedSteps = new Set([3, 3.5, 4, 5, 6, 7, 8, 5.5, 5.6, 7.5]);
        if (allowedSteps.has(normalizedTargetStep)) {
          const isPomStep = normalizedTargetStep === 5.5 || normalizedTargetStep === 5.6;
          if (isPomStep && !activeWorkflow.isTestCaseApprovedForCurrentSection) {
            activeWorkflow.setCurrentStep(6);
          } else {
            activeWorkflow.setCurrentStep(normalizedTargetStep);
            if (normalizedTargetStep === 4) {
              generationRestoreAttemptRef.current = `generation:${targetPdfId}:${targetBulkJobId || 'latest'}`;
              if (targetBulkJobId) {
                await activeWorkflow.restoreBulkGenerationById(targetBulkJobId, { expectedPdfId: targetPdfId });
              } else {
                await activeWorkflow.restoreLatestBulkGeneration(targetPdfId);
              }
            }
          }
        } else {
          activeWorkflow.setCurrentStep(hasSectionTarget ? 6 : 3);
        }
      } catch (_err) {
        if (cancelled) return;
        activeWorkflow.setError('Failed to load workflow route: ' + (_err?.message || 'Unable to restore this PDF'));
        setRouteHydrationFailedSignature(hydrationSignature);
        // Deep-link open is best-effort and should not break regular workflow.
      } finally {
        finished = true;
        if (cancelled) {
          finishRouteHydration(hydrationSignature, hydrationToken);
          return;
        }
        window.setTimeout(() => {
          finishRouteHydration(hydrationSignature, hydrationToken);
        }, 0);
      }
    };

    run();
    return () => {
      cancelled = true;
      if (!finished && deepLinkHandledRef.current === signature) {
        deepLinkHandledRef.current = '';
      }
      finishRouteHydration(hydrationSignature, hydrationToken);
    };
  }, [beginRouteHydration, finishRouteHydration, location.search]);

  const parsedPathTarget = React.useMemo(
    () => normalizePdfWorkflowPath(
      location.pathname,
      routeRoot,
      normalizedApplicationId
    ),
    [location.pathname, normalizedApplicationId, routeRoot]
  );

  React.useEffect(() => {
    if (
      !isExcelTestCasesWorkflow
      || !parsedPathTarget.isPdfRoute
      || !parsedPathTarget.importId
      || location.search
    ) {
      return undefined;
    }
    if (routeHydrationRef.current) {
      return undefined;
    }

    const targetImportId = String(parsedPathTarget.importId || '').trim();
    const signature = `import:${targetImportId}`;
    const activeWorkflow = workflowRef.current;
    const activeImportId = String(activeWorkflow.excelImportAnalysis?.import_id || '').trim();
    const alreadyOnTargetImport =
      Number(activeWorkflow.currentStep) === 1.5 && activeImportId === targetImportId;

    if (pathLinkHandledRef.current === signature && alreadyOnTargetImport) {
      return undefined;
    }

    pathLinkHandledRef.current = signature;
    const hydrationToken = beginRouteHydration(signature);

    let cancelled = false;
    let finished = false;
    const run = async () => {
      try {
        await activeWorkflow.restoreExcelTestCaseImport(targetImportId);
      } catch (_err) {
        activeWorkflow.setError(
          'Failed to load Excel import route: ' + (_err?.message || 'Unable to restore this workbook import')
        );
        setRouteHydrationFailedSignature(signature);
      } finally {
        finished = true;
        if (cancelled) {
          finishRouteHydration(signature, hydrationToken);
          return;
        }
        window.setTimeout(() => {
          finishRouteHydration(signature, hydrationToken);
        }, 0);
      }
    };

    run();
    return () => {
      cancelled = true;
      if (!finished && pathLinkHandledRef.current === signature) {
        pathLinkHandledRef.current = '';
      }
      finishRouteHydration(signature, hydrationToken);
    };
  }, [
    beginRouteHydration,
    finishRouteHydration,
    isExcelTestCasesWorkflow,
    location.search,
    parsedPathTarget.importId,
    parsedPathTarget.isPdfRoute,
  ]);

  React.useEffect(() => {
    if (!parsedPathTarget.isPdfRoute) return;

    const isSectionReviewRoute =
      Number(parsedPathTarget.sectionNumber) > 0
      && [5, 6].includes(Number(parsedPathTarget.step));
    const routeReturnTarget = isSectionReviewRoute
      ? parseSectionReviewReturnTarget(location.search, parsedPathTarget.pdfId)
      : null;

    if (routeReturnTarget) {
      setSectionReviewReturnTarget((prev) =>
        isSameSectionReviewReturnTarget(prev, routeReturnTarget) ? prev : routeReturnTarget
      );
      return;
    }

    if (!isSectionReviewRoute) {
      setSectionReviewReturnTarget(null);
    }
  }, [
    location.search,
    parsedPathTarget.isPdfRoute,
    parsedPathTarget.pdfId,
    parsedPathTarget.sectionNumber,
    parsedPathTarget.step,
  ]);

  React.useEffect(() => {
    if (!parsedPathTarget.isPdfRoute || !parsedPathTarget.pdfId) {
      return undefined;
    }
    if (routeHydrationRef.current) {
      return undefined;
    }

    const targetStep = Number(parsedPathTarget.step);
    const targetSection = parsedPathTarget.sectionNumber;
    const targetBulkJobId = String(parsedPathTarget.bulkJobId || '').trim();
    const targetPlanningJobId = String(new URLSearchParams(location.search || '').get('planning_job_id') || '').trim();
    const openedFromHistory = isHistorySourceRoute(location.search);
    const hasSectionTarget = Number.isFinite(targetSection) && targetSection > 0;
    const signature = `path:${parsedPathTarget.pdfId}:${hasSectionTarget ? targetSection : 'none'}:${targetBulkJobId || 'none'}:${targetPlanningJobId || 'none'}:${targetStep}:${openedFromHistory ? 'history' : 'direct'}`;
    const activeWorkflow = workflowRef.current;

    const alreadyOnTargetPdf = activeWorkflow.pdfId === parsedPathTarget.pdfId;
    const alreadyOnTargetSection =
      !hasSectionTarget || Number(activeWorkflow.selectedSection) === Number(targetSection);
    const alreadyOnTargetBulkJob =
      targetStep !== 4 || !targetBulkJobId || String(activeWorkflow.bulkJob?.id || '').trim() === targetBulkJobId;
    const alreadyOnTargetStep = Number(activeWorkflow.currentStep) === targetStep;

    // A state-driven step change renders once with the previous URL before
    // route sync writes the new path. That old path has already been handled;
    // hydrating it again would send the user back to the previous step.
    if (pathLinkHandledRef.current === signature) {
      return undefined;
    }

    pathLinkHandledRef.current = signature;
    const hydrationToken = beginRouteHydration(signature);

    let cancelled = false;
    let finished = false;
    const restorePlanningResult = async (persistedPlan = null) => {
      if (targetStep !== 3.5 || persistedPlan?.plan) return;
      if (!targetPlanningJobId) {
        throw new Error('No saved plan was found for this section. Run Analyze & Plan again.');
      }
      const snapshot = await loadJobSnapshot(targetPlanningJobId);
      if (cancelled) return;
      const planningItem = snapshot.items.find(
        (item) => Number(item?.section_number) === Number(targetSection)
      );
      const planningResult = planningItem?.result_payload || planningItem?.result_summary;
      if (
        snapshot.job.type !== 'test_pre_analysis'
        || String(snapshot.job.source_id || '') !== String(parsedPathTarget.pdfId)
        || !planningResult?.plan
      ) {
        throw new Error('The completed section plan could not be restored.');
      }
      activeWorkflow.restorePreAnalysisPlan(planningResult, { sectionNumber: targetSection });
    };
    const run = async () => {
      let sectionRestored = alreadyOnTargetPdf && alreadyOnTargetSection;
      try {
        if (alreadyOnTargetPdf && alreadyOnTargetSection && alreadyOnTargetBulkJob) {
          await restorePlanningResult(activeWorkflow.preAnalysisPlan);
          if (!alreadyOnTargetStep) {
            activeWorkflow.setCurrentStep(targetStep);
          }
          if (targetStep === 4 && !activeWorkflow.bulkJob) {
            const restoreKey = `generation:${parsedPathTarget.pdfId}:${targetBulkJobId || 'latest'}`;
            generationRestoreAttemptRef.current = restoreKey;
            if (targetBulkJobId) {
              await activeWorkflow.restoreBulkGenerationById(targetBulkJobId, { expectedPdfId: parsedPathTarget.pdfId });
            } else {
              await activeWorkflow.restoreLatestBulkGeneration(parsedPathTarget.pdfId);
            }
          }
          return;
        }

        const hydratedWorkflow = await activeWorkflow.hydratePdfWorkflow({
          pdf: {
            pdf_id: parsedPathTarget.pdfId,
            pdf_file: parsedPathTarget.pdfId,
          },
          sectionNumber: hasSectionTarget ? targetSection : null,
          targetStep,
          markLoadedFromHistory: openedFromHistory,
          isStale: () => cancelled,
        });

        if (cancelled) return;
        sectionRestored = hasSectionTarget && Boolean(hydratedWorkflow);
        await restorePlanningResult(hydratedWorkflow?.preAnalysisPlan);
        if (cancelled) return;
        activeWorkflow.setCurrentStep(targetStep);
        if (targetStep === 4) {
          const restoreKey = `generation:${parsedPathTarget.pdfId}:${targetBulkJobId || 'latest'}`;
          generationRestoreAttemptRef.current = restoreKey;
          if (targetBulkJobId) {
            await activeWorkflow.restoreBulkGenerationById(targetBulkJobId, { expectedPdfId: parsedPathTarget.pdfId });
          } else {
            await activeWorkflow.restoreLatestBulkGeneration(parsedPathTarget.pdfId);
          }
        }
      } catch (_err) {
        if (cancelled) return;
        activeWorkflow.setError(
          'Failed to load workflow route: ' + (_err?.message || 'Unable to restore this PDF')
        );
        setRouteHydrationFailedSignature(signature);
        // A plan or output route with nothing to restore would render an empty
        // step (or fall back to intake); land on the section's selection screen
        // so it can be analyzed or generated again.
        if ((targetStep === 3.5 && sectionRestored) || _err?.pdfRestored) {
          replaceNextRouteSyncRef.current = true;
          activeWorkflow.setCurrentStep(3);
        }
        // Path route hydration is best-effort and should not block regular workflow use.
      } finally {
        finished = true;
        if (cancelled) {
          finishRouteHydration(signature, hydrationToken);
          return;
        }
        window.setTimeout(() => {
          finishRouteHydration(signature, hydrationToken);
        }, 0);
      }
    };

    run();
    return () => {
      cancelled = true;
      if (!finished && pathLinkHandledRef.current === signature) {
        pathLinkHandledRef.current = '';
      }
      finishRouteHydration(signature, hydrationToken);
    };
  }, [beginRouteHydration, finishRouteHydration, location.search, parsedPathTarget]);

  React.useEffect(() => {
    if (!parsedPathTarget.isPdfRoute || !parsedPathTarget.pdfId || Number(parsedPathTarget.step) !== 4) {
      return;
    }
    const routeBulkJobId = String(parsedPathTarget.bulkJobId || '').trim();
    if (
      activeBulkLoading ||
      (activeBulkJob && (!routeBulkJobId || String(activeBulkJob.id || '').trim() === routeBulkJobId))
    ) {
      return;
    }

    const restoreKey = `generation:${parsedPathTarget.pdfId}:${routeBulkJobId || 'latest'}`;
    if (generationRestoreAttemptRef.current === restoreKey) {
      return;
    }
    generationRestoreAttemptRef.current = restoreKey;
    if (routeBulkJobId) {
      void restoreBulkGenerationById(routeBulkJobId, { expectedPdfId: parsedPathTarget.pdfId });
    } else {
      void restoreLatestBulkGeneration(parsedPathTarget.pdfId);
    }
  }, [
    activeBulkJob,
    activeBulkLoading,
    parsedPathTarget.isPdfRoute,
    parsedPathTarget.bulkJobId,
    parsedPathTarget.pdfId,
    parsedPathTarget.step,
    restoreBulkGenerationById,
    restoreLatestBulkGeneration,
  ]);

  React.useEffect(() => {
    if (!parsedPathTarget.isPdfRoute || routeHydrationRef.current) return;
    if (routeHydrationFailedSignature && parsedPathTarget.pdfId && !workflow.pdfId) return;

    const routeSectionNumber =
      workflow.currentStep === 4 && (workflow.bulkJob || ['processing', 'bulk_processing'].includes(workflow.status) || workflow.bulkLoading)
        ? null
        : (
            workflow.selectedSection
            || workflow.processingResult?.section_number
            || parsedPathTarget.sectionNumber
          );
    const routeImportId = Number(workflow.currentStep) === 1.5
      ? String(workflow.excelImportAnalysis?.import_id || parsedPathTarget.importId || '').trim()
      : '';
    const expectedPath = buildPdfWorkflowPath({
      pdfId: workflow.pdfId,
      sectionNumber: routeSectionNumber,
      step: workflow.currentStep,
      bulkJobId: workflow.currentStep === 4 ? workflow.bulkJob?.id : '',
      importId: routeImportId,
      routeRoot,
      applicationId: normalizedApplicationId,
    });
    const shouldCarryReviewReturn =
      sectionReviewReturnTarget?.type === 'bulk_generation'
      && [5, 6].includes(Number(workflow.currentStep));
    let expectedSearch = shouldCarryReviewReturn
      ? buildSectionReviewReturnSearch(sectionReviewReturnTarget)
      : workflow.brdLoadedFromHistory
        ? buildHistorySourceSearch()
        : '';
    const planningJobId = Number(workflow.currentStep) === 3.5
      ? String(new URLSearchParams(location.search || '').get('planning_job_id') || '').trim()
      : '';
    if (planningJobId) {
      const expectedParams = new URLSearchParams(expectedSearch);
      expectedParams.set('planning_job_id', planningJobId);
      expectedSearch = `?${expectedParams.toString()}`;
    }
    const expectedLocation = `${expectedPath}${expectedSearch}`;
    const currentLocation = `${location.pathname}${location.search || ''}`;

    const workflowEntryPath = applicationRouteMode
      ? buildPdfWorkflowPath({
          step: 1,
          routeRoot,
          applicationId: normalizedApplicationId,
        })
      : `/${routeRoot}`;
    if (expectedLocation !== workflowEntryPath) {
      sessionStorage.setItem(workflow.workflowLastRouteSessionKey, expectedLocation);
    } else if (!workflow.pdfId && !workflow.excelImportAnalysis?.import_id) {
      sessionStorage.removeItem(workflow.workflowLastRouteSessionKey);
    }

    const replaceRoute = replaceNextRouteSyncRef.current;
    replaceNextRouteSyncRef.current = false;
    if (currentLocation !== expectedLocation) {
      // Same screen, different query (e.g. `source=history` being added), or a
      // route that could not be restored: rewrite in place so browser Back
      // does not bounce into the same state again.
      navigate(expectedLocation, { replace: replaceRoute || expectedPath === location.pathname });
    }
  }, [
    location.search,
    location.pathname,
    navigate,
    parsedPathTarget,
    sectionReviewReturnTarget,
    sectionReviewReturnTarget?.bulkJobId,
    sectionReviewReturnTarget?.pdfId,
    sectionReviewReturnTarget?.type,
    workflow.bulkJob,
    workflow.bulkJob?.id,
    workflow.bulkLoading,
    workflow.brdLoadedFromHistory,
    workflow.currentStep,
    workflow.excelImportAnalysis?.import_id,
    workflow.pdfId,
    workflow.processingResult?.section_number,
    workflow.selectedSection,
    workflow.status,
    workflow.workflowLastRouteSessionKey,
    applicationRouteMode,
    normalizedApplicationId,
    routeHydratingSignature,
    routeHydrationFailedSignature,
    routeRoot,
  ]);

  const mergeApprovedFocus = (nextFocus = []) => {
    const merged = [
      ...(workflow.approvedFocus || []),
      ...(Array.isArray(nextFocus) ? nextFocus : []),
    ]
      .map((item) => String(item || '').trim())
      .filter(Boolean);
    return Array.from(new Set(merged));
  };

  const mergeApprovedGlobalConstraints = (nextConstraints = []) => {
    const merged = [
      ...(workflow.approvedGlobalConstraints || []),
      ...(Array.isArray(nextConstraints) ? nextConstraints : []),
    ]
      .map((item) => String(item || '').trim())
      .filter(Boolean);
    return Array.from(new Set(merged));
  };

  const handleGenerateMore = async (request) => {
    const additionalPositive = Number(request?.positive_count || 0);
    const additionalNegative = Number(request?.negative_count || 0);
    if (additionalPositive + additionalNegative <= 0) {
      throw new Error('Add at least one additional positive or negative case.');
    }

    const nextApprovedCounts = {
      positive_count: Math.max(
        0,
        Number(workflow.approvedCounts?.positive_count || 0) + additionalPositive
      ),
      negative_count: Math.max(
        0,
        Number(workflow.approvedCounts?.negative_count || 0) + additionalNegative
      ),
    };
    const nextFocus = mergeApprovedFocus(request?.additional_focus || []);
    const nextGlobalConstraints = mergeApprovedGlobalConstraints(
      request?.global_constraints || []
    );

    workflow.setApprovedCounts(nextApprovedCounts);
    workflow.setApprovedFocus(nextFocus);
    workflow.setApprovedGlobalConstraints(nextGlobalConstraints);

    const appendResponse = await workflow.generateAdditionalTestCases({
      positive_count: additionalPositive,
      negative_count: additionalNegative,
      additional_focus: nextFocus,
      global_constraints: nextGlobalConstraints,
      existing_test_cases: request?.existing_test_cases || null,
    });
    return appendResponse;
  };

  const generationStatus = String(workflow.processingResult?.stream_status || '').trim().toLowerCase();
  const isTestCaseGenerationLocked = workflow.currentStep === 5 && (
    generationStatus === 'running' || workflow.status === 'processing'
  );
  const hasWorkflowBack = applicationRouteMode
    || Boolean(getPreviousWorkflowStep(workflow.currentStep));
  const documentMetricValue = workflow.documentName || workflow.file?.name || (
    workflow.pdfId ? 'Loading document...' : 'Not selected'
  );
  const routeSearchParams = new URLSearchParams(location.search || '');
  const queryTargetPdfId = String(routeSearchParams.get('pdf_id') || '').trim();
  const queryTargetSectionNumber = Number(routeSearchParams.get('section'));
  const queryTargetStep = Number(routeSearchParams.get('step'));
  const queryTargetBulkJobId = String(
    routeSearchParams.get('bulk_job_id') || routeSearchParams.get('bulkJobId') || ''
  ).trim();
  const routeArtifactVersion = Number(routeSearchParams.get('artifact_version') || routeSearchParams.get('artifactVersion'));
  const hasQuerySectionTarget =
    Number.isFinite(queryTargetSectionNumber) && queryTargetSectionNumber > 0;
  const queryRouteHydrationSignature = queryTargetPdfId
    ? `query:${queryTargetPdfId}:${hasQuerySectionTarget ? queryTargetSectionNumber : 'none'}:${queryTargetBulkJobId || 'none'}:${Number.isFinite(queryTargetStep) ? queryTargetStep : 'default'}`
    : '';
  const pathTargetPdfId = parsedPathTarget.isPdfRoute ? String(parsedPathTarget.pdfId || '').trim() : '';
  const pathTargetImportId = parsedPathTarget.isPdfRoute ? String(parsedPathTarget.importId || '').trim() : '';
  const pathTargetSectionNumber = Number(parsedPathTarget.sectionNumber);
  const hasPathSectionTarget =
    Number.isFinite(pathTargetSectionNumber) && pathTargetSectionNumber > 0;
  const pathTargetStep = Number(parsedPathTarget.step);
  const pathTargetPlanningJobId = String(routeSearchParams.get('planning_job_id') || '').trim();
  const pathRouteHydrationSignature = pathTargetPdfId
    ? `path:${pathTargetPdfId}:${hasPathSectionTarget ? pathTargetSectionNumber : 'none'}:${String(parsedPathTarget.bulkJobId || '').trim() || 'none'}:${pathTargetPlanningJobId || 'none'}:${pathTargetStep}`
    : pathTargetImportId
      ? `import:${pathTargetImportId}`
    : '';
  const currentRouteHydrationSignature = queryRouteHydrationSignature || pathRouteHydrationSignature;
  const currentRouteHydrationFailed = Boolean(currentRouteHydrationSignature) && (
    routeHydrationFailedSignature === currentRouteHydrationSignature
    || routeHydrationFailedSignature.startsWith(`${currentRouteHydrationSignature}:`)
  );
  const currentRouteTargetPdfId = queryTargetPdfId || pathTargetPdfId;
  const currentRouteTargetImportId = pathTargetImportId;
  const currentRouteTargetSectionNumber = queryTargetPdfId
    ? (hasQuerySectionTarget ? queryTargetSectionNumber : null)
    : (hasPathSectionTarget ? pathTargetSectionNumber : null);
  const activeRouteSectionNumber = Number(
    workflow.selectedSection || workflow.processingResult?.section_number || 0
  );
  const routeTargetNeedsHydration = currentRouteTargetImportId
    ? (
        Number(workflow.currentStep) !== 1.5 ||
        String(workflow.excelImportAnalysis?.import_id || '').trim() !== currentRouteTargetImportId
      )
    : Boolean(currentRouteTargetPdfId) && (
      String(workflow.pdfId || '').trim() !== currentRouteTargetPdfId ||
      (
        Number.isFinite(currentRouteTargetSectionNumber) &&
        currentRouteTargetSectionNumber > 0 &&
        activeRouteSectionNumber !== currentRouteTargetSectionNumber
      )
    );
  const shouldShowRouteHydrationSkeleton = Boolean(currentRouteHydrationSignature) &&
    !currentRouteHydrationFailed &&
    (
      routeHydratingSignature === currentRouteHydrationSignature ||
      routeTargetNeedsHydration
    );

  const handlePomInternalBackChange = React.useCallback((hasInternalBack, handler) => {
    pomInternalBackRef.current = typeof handler === 'function' ? handler : null;
    setPomHasInternalBack(Boolean(hasInternalBack));
  }, []);

  const continueToAutomationScripts = React.useCallback(() => {
    setShowAutomationLoginPrompt(false);
    setShowAutomationCaptureModal(false);
    workflow.setCurrentStep(7);
  }, [workflow]);

  const pomWorkflowReturnPath = React.useMemo(() => {
    const sectionNumber = workflow.selectedSection
      || workflow.processingResult?.section_number
      || parsedPathTarget.sectionNumber;
    const path = buildPdfWorkflowPath({
      pdfId: workflow.pdfId || parsedPathTarget.pdfId,
      sectionNumber,
      step: 5.5,
      routeRoot,
      applicationId: normalizedApplicationId,
    });
    return path || `${location.pathname}${location.search || ''}${location.hash || ''}`;
  }, [
    location.hash,
    location.pathname,
    location.search,
    normalizedApplicationId,
    parsedPathTarget.pdfId,
    parsedPathTarget.sectionNumber,
    routeRoot,
    workflow.pdfId,
    workflow.processingResult?.section_number,
    workflow.selectedSection,
  ]);

  const buildApplicationPomsReturnSearch = React.useCallback(() => {
    const params = new URLSearchParams();
    params.set('returnTo', pomWorkflowReturnPath);
    return params.toString();
  }, [pomWorkflowReturnPath]);

  const handleManageApplicationPoms = React.useCallback(() => {
    if (!applicationRouteMode || !normalizedApplicationId) return;
    navigate(`${buildApplicationPomsPath(normalizedApplicationId)}?${buildApplicationPomsReturnSearch()}`);
  }, [
    applicationRouteMode,
    buildApplicationPomsReturnSearch,
    navigate,
    normalizedApplicationId,
  ]);

  const handleOpenApplicationPom = React.useCallback((pom, mode = 'view') => {
    if (!applicationRouteMode || !normalizedApplicationId) return;
    const pageName = String(pom?.pageName || pom?.page_name || '').trim();
    if (!pageName) return;
    navigate(
      `${buildApplicationPomEditorPath(normalizedApplicationId, pageName, mode)}?${buildApplicationPomsReturnSearch()}`
    );
  }, [
    applicationRouteMode,
    buildApplicationPomsReturnSearch,
    navigate,
    normalizedApplicationId,
  ]);

  const handleContinueToAutomation = React.useCallback(async () => {
    if (automationAuthChecking) return;
    const normalizedPdfId = automationAuthSourceId;
    if (!normalizedPdfId) {
      setShowAutomationLoginPrompt(true);
      return;
    }

    setAutomationAuthChecking(true);
    try {
      const response = await apiFetch(API_ENDPOINTS.PLAYWRIGHT_AUTH_STATE(normalizedPdfId));
      if (response.ok) {
        const payload = await response.json().catch(() => ({}));
        if (payload?.available || payload?.metadata?.login_replay_available) {
          continueToAutomationScripts();
          return;
        }
      }
    } catch (_err) {
      // Fall through to the explicit login question if status lookup is unavailable.
    } finally {
      setAutomationAuthChecking(false);
    }
    setShowAutomationLoginPrompt(true);
  }, [automationAuthChecking, automationAuthSourceId, continueToAutomationScripts]);

  const handleWorkflowBack = async () => {
    if (isTestCaseGenerationLocked) {
      return;
    }

    const returnToWorkflowRoot = () => {
      workflow.resetWorkflow();
      navigate(
        applicationRouteMode
          ? buildPdfWorkflowPath({
              step: 1,
              routeRoot,
              applicationId: normalizedApplicationId,
            })
          : `/${routeRoot}`,
        { replace: true }
      );
    };

    const returnToSourceReview = () => {
      if (isTextRequirementsWorkflow || workflow.isOpenApiSource || isApiTestingWorkflow || workflow.isExcelSource) {
        returnToWorkflowRoot();
        return;
      }
      workflow.returnToExtractionStep();
    };

    if (applicationRouteMode && workflow.currentStep === 1) {
      workflow.resetWorkflow();
      navigate(moduleDashboardPath);
      return;
    }

    if (workflow.currentStep === 1.5) {
      await workflow.cancelExcelTestCaseImport();
      return;
    }

    if ((workflow.currentStep === 5.5 || workflow.currentStep === 5.6) && pomHasInternalBack && pomInternalBackRef.current) {
      pomInternalBackRef.current();
      return;
    }

    if (!applicationRouteMode) {
      const previousRoute = consumeWorkspaceBackRoute(
        `${location.pathname}${location.search || ''}${location.hash || ''}`,
        {
          allowedRoutePrefixes: [`/${routeRoot}`],
          ignoreStoredStack: wasCurrentDocumentReloaded(),
        }
      );
      if (previousRoute) {
        if (previousRoute === `/${routeRoot}`) {
          returnToWorkflowRoot();
          return;
        }
        navigate(previousRoute);
        return;
      }
    }

    if (workflow.currentStep === 2) {
      returnToWorkflowRoot();
      return;
    }

    if (workflow.currentStep === 3) {
      returnToSourceReview();
      return;
    }

    if (workflow.currentStep === 3.5) {
      workflow.setCurrentStep(3);
      return;
    }

    if (workflow.currentStep === 4) {
      workflow.setCurrentStep(3);
      return;
    }

    if (workflow.currentStep === 5) {
      if (workflow.isExcelSource) {
        // A committed import already has a source id. Resetting the active
        // source avoids the route layer interpreting `/pdf/:id` as the BRD
        // extraction step when the user chooses to start another import.
        returnToWorkflowRoot();
        return;
      }
      if (
        sectionReviewReturnTarget?.type === 'bulk_generation'
        && String(sectionReviewReturnTarget.bulkJobId || '').trim()
      ) {
        const targetBulkJobId = String(sectionReviewReturnTarget.bulkJobId).trim();
        const targetPdfId = String(sectionReviewReturnTarget.pdfId || workflow.pdfId || '').trim();
        setSectionReviewReturnTarget(null);
        workflow.setSelectedSection(null);
        workflow.setProcessingResult(null);
        workflow.setEditedTestCases([]);
        workflow.setOriginalTestCases([]);
        workflow.setCurrentStep(4);
        await workflow.restoreBulkGenerationById(targetBulkJobId, {
          expectedPdfId: targetPdfId || workflow.pdfId,
        });
        return;
      }
      if (workflow.preAnalysisPlan) {
        workflow.setCurrentStep(3.5);
      } else {
        workflow.setCurrentStep(3);
      }
      return;
    }

    if (workflow.currentStep === 5.5 || workflow.currentStep === 5.6) {
      workflow.setCurrentStep(6);
      return;
    }

    if (workflow.currentStep === 6) {
      workflow.setCurrentStep(5);
      return;
    }

    if (workflow.currentStep === 7 && (workflow.isOpenApiSource || isApiTestingWorkflow)) {
      workflow.setCurrentStep(6);
      return;
    }

    if (workflow.currentStep === 7) {
      workflow.setCurrentStep(5.5);
      return;
    }

    if (workflow.currentStep === 7.5) {
      workflow.setCurrentStep(5.6);
      return;
    }

    if (workflow.currentStep === 8) {
      workflow.setCurrentStep(
        (workflow.testingType === 'mobile' || workflow.testingType === 'browserstack') ? 7.5 : 7
      );
      return;
    }

    const previousStep = getPreviousWorkflowStep(workflow.currentStep);
    if (previousStep) {
      if (workflow.currentStep === 3 && previousStep === 2) {
        returnToSourceReview();
        return;
      }
      workflow.setCurrentStep(previousStep);
      return;
    }
  };

  if (shouldShowRouteHydrationSkeleton || (workflow.historyRestoreLoading && !workflow.showPreviousPDFs)) {
    return <PDFRouteHydrationSkeleton theme={workspaceVariant.theme} />;
  }

  if (currentRouteHydrationFailed && currentRouteTargetPdfId && !workflow.pdfId) {
    return (
      <main className="mx-auto flex min-h-[70vh] max-w-2xl items-center px-5 py-12">
        <section className="w-full rounded-2xl border border-rose-200 bg-white p-6 shadow-lg sm:p-8" role="alert">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
            <AlertTriangle className="h-6 w-6" />
          </span>
          <h1 className="mt-5 text-2xl font-bold text-slate-900">Could not open this document</h1>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            {workflow.error || 'The saved document could not be loaded. Please try again.'}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              className="primary-btn px-4 py-2"
              onClick={() => {
                pathLinkHandledRef.current = '';
                deepLinkHandledRef.current = '';
                setRouteHydrationFailedSignature('');
                navigate(`${location.pathname}${location.search ? `${location.search}&` : '?'}retry=${Date.now()}`, { replace: true });
              }}
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </button>
            <button type="button" className="secondary-btn px-4 py-2" onClick={() => navigate(moduleDashboardPath)}>
              Back to documents
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <>
      <WorkspacePage
        theme={workspaceVariant.theme}
        containerClassName={workflow.currentStep === 7 ? 'max-w-[1720px]' : undefined}
        sidebar={(
          <WorkspaceSidebar
            theme={workspaceVariant.theme}
            kicker={workspaceVariant.sidebarKicker}
            title={workspaceVariant.sidebarTitle}
            description={workspaceVariant.sidebarDescription}
            historyLoading={workflow.previousPDFsLoading}
            onHistory={workflow.loadPreviousPDFs}
            action={applicationRouteMode ? {
              label: 'Dashboard',
              icon: LayoutDashboard,
              onClick: () => navigate(moduleDashboardPath),
            } : null}
            metrics={[
              {
                label: workspaceVariant.sourceMetricLabel,
                value: documentMetricValue,
              },
              {
                label: workspaceVariant.unitMetricLabel,
                value: workflow.pdfId ? workflow.sections.length : 'Not selected',
              },
              {
                label: 'Processing Mode',
                value: workspaceVariant.processingLabel
                  || formatModeLabel(),
              },
              {
                label: 'Current Stage',
                value: currentPhase.label,
              },
            ]}
          />
        )}
      >
        {workflow.error && (
          <div className="mb-6">
            <ErrorAlert error={workflow.error} />
          </div>
        )}

        <WorkspaceStageCard
          kicker="Current Stage"
          title={currentPhase.label}
          description={currentPhase.description}
          actions={(
            <>
              {hasWorkflowBack && (
                <WorkspaceBackButton
                  onClick={handleWorkflowBack}
                  disabled={isTestCaseGenerationLocked}
                  title={
                    isTestCaseGenerationLocked
                      ? 'Back is available after test case generation finishes.'
                      : 'Go back'
                  }
                  size="xs"
                  ariaLabel={
                    isTestCaseGenerationLocked
                      ? 'Back is disabled while test cases are generating'
                      : 'Go back'
                  }
                />
              )}
            </>
          )}
          status={{ icon: Bot, label: 'AI-assisted workflow' }}
          checklist={(
            <WorkspaceChecklist
              title={currentChecklist.title}
              items={currentChecklist.items}
            />
          )}
        >
                  {workflow.currentStep === 1 && (
                    <PDFUploadStep
                      intakeMode={intakeMode}
                      file={workflow.file}
                      openApiFile={workflow.openApiFile}
                      excelFile={workflow.excelFile}
                      manualInputTitle={workflow.manualInputTitle}
                      manualInputText={workflow.manualInputText}
                      manualInputImages={workflow.manualInputImages}
                      status={workflow.status}
                      activeUploadSource={workflow.activeUploadSource}
                      onFileChange={workflow.handleFileChange}
                      onUpload={workflow.uploadPDF}
                      onOpenApiFileChange={workflow.handleOpenApiFileChange}
                      onUploadOpenApi={workflow.uploadOpenApi}
                      onExcelFileChange={workflow.handleExcelFileChange}
                      onAnalyzeExcel={workflow.analyzeExcelTestCases}
                      onManualInputTitleChange={workflow.setManualInputTitle}
                      onManualInputTextChange={workflow.setManualInputText}
                      onManualInputImagesChange={workflow.handleManualInputImagesChange}
                      onUploadTextImages={workflow.uploadTextImages}
                      workspaces={workflow.workspaces}
                      selectedWorkspaceId={workflow.selectedWorkspaceId}
                      workspaceLoading={workflow.workspaceLoading}
                      workspaceError={workflow.workspaceError}
                      onSelectWorkspace={workflow.setSelectedWorkspaceId}
                      onCreateWorkspace={workflow.createWorkspace}
                      onManageWorkspaceMembers={workflow.openWorkspaceMembersManager}
                      canCreateWorkspace={workflow.canCreateWorkspace}
                      canViewWorkspaceMembers={workflow.canViewWorkspaceMembers}
                      applicationLocked={workflow.applicationLocked}
                    />
                  )}

                  {workflow.currentStep === 1.5 && workflow.excelImportAnalysis && (
                    <ExcelTestCaseImportStep
                      analysis={workflow.excelImportAnalysis}
                      file={workflow.excelFile}
                      workspaceId={workflow.selectedWorkspaceId}
                      onBack={workflow.cancelExcelTestCaseImport}
                      onPreview={workflow.previewExcelTestCases}
                      onCommit={workflow.commitExcelTestCases}
                    />
                  )}

                  {workflow.currentStep === 2 && (
                    <SectionExtractionStep
                      status={workflow.status}
                      bulkJob={workflow.bulkJob}
                      bulkItems={workflow.bulkItems}
                      dgsConfig={workflow.dgsConfig}
                      reusableSections={workflow.reusableSections}
                      brdChangeAnalysis={workflow.brdChangeAnalysis}
                      brdUpdatePending={workflow.brdUpdatePending}
                      canUploadUpdatedBrd={
                        workflow.brdLoadedFromHistory
                        && !workflow.selectedPreviousPdf?.brd_update_blocked_reason
                        && workflow.selectedPreviousPdf?.brd_update_supported !== false
                      }
                      brdUpdateBlockedReason={workflow.selectedPreviousPdf?.brd_update_blocked_reason || ''}
                      baseBrd={workflow.selectedPreviousPdf}
                      documentName={workflow.documentName}
                      pdfId={workflow.pdfId}
                      extractionMode={workflow.pdfExtractionMode}
                      onUploadUpdatedBrd={workflow.uploadUpdatedBrdVersion}
                      onUploadDgs={workflow.parseDgsSheet}
                      onDownloadDgsTemplate={workflow.downloadDgsTemplate}
                      onClearDgs={workflow.clearDgsConfig}
                      onExtractionModeChange={workflow.setPdfExtractionMode}
                      onExtract={workflow.extractSections}
                      onUseReusableSections={workflow.useReusableSections}
                      onBrdChangeAnalysisUpdate={workflow.setBrdChangeAnalysis}
                      onTocStructureApproved={() => workflow.setCurrentStep(3)}
                    />
                  )}

                  {workflow.currentStep === 3 && (
                    <SectionSelectionStep
                      sections={workflow.sections}
                      rawSectionOutline={workflow.rawSectionOutline}
                      sectionGrouping={workflow.sectionGrouping}
                      isOpenApiSource={workflow.isOpenApiSource}
                      brdChangeAnalysis={workflow.brdChangeAnalysis}
                      brdUpdatePending={workflow.brdUpdatePending}
                      pdfId={workflow.pdfId}
                      selectedSection={workflow.selectedSection}
                      selectedSectionArtifact={
                        workflow.selectedSection
                          ? workflow.sectionArtifactIndex?.[workflow.selectedSection]
                          : null
                      }
                      sectionArtifactIndex={workflow.sectionArtifactIndex}
                      approvalStatus={workflow.approvalStatus}
                      approvalLoading={workflow.approvalLoading}
                      status={workflow.status}
                      bulkMode={workflow.bulkMode}
                      bulkSelectedSections={workflow.bulkSelectedSections}
                      bulkJob={workflow.bulkJob}
                      bulkItems={workflow.bulkItems}
                      bulkLoading={workflow.bulkLoading}
                      bulkError={workflow.bulkError}
                      onSelectSections={() => workflow.setCurrentStep(3)}
                      onRetryFailed={workflow.retryFailedGenerationSections}
                      onSelectSection={(sectionNum) => {
                        setSectionReviewReturnTarget(null);
                        workflow.setSelectedSection(sectionNum);
                        workflow.setPreAnalysisPlan(null);
                        workflow.setConfirmedReplacementSection(null);
                        workflow.setBulkMode('single');
                      }}
                      onBulkModeChange={(mode) => {
                        setSectionReviewReturnTarget(null);
                        const normalizedMode = mode === 'bulk' ? 'bulk' : 'single';
                        workflow.setBulkMode(normalizedMode);
                        workflow.setPreAnalysisPlan(null);
                        workflow.setConfirmedReplacementSection(null);
                        if (normalizedMode === 'single' && workflow.selectedSection) {
                          workflow.setBulkSelectedSections([workflow.selectedSection]);
                        }
                      }}
                      onToggleBulkSection={workflow.toggleBulkSectionSelection}
                      onSelectAllBulkSections={workflow.selectAllBulkSections}
                      onClearBulkSelection={workflow.clearBulkSelection}
                      onStartBulkGeneration={workflow.startBulkGeneration}
                      onViewBulkGeneration={() => workflow.setCurrentStep(4)}
                      onRestoreSkippedTocSections={workflow.restoreSkippedTocSections}
                      onRemoveRestoredTocSections={workflow.removeRestoredTocSections}
                      onApplyRebuiltToc={workflow.applyRebuiltToc}
                      onProcess={workflow.preAnalyzeSection}
                      onOpenSavedPlan={workflow.openSavedPreAnalysisPlan}
                      onRequestApproval={workflow.requestBrdApproval}
                      onRemindApproval={workflow.remindBrdApproval}
                      onApprove={workflow.approveBrd}
                      onReject={workflow.rejectBrd}
                      onDownloadExtractedSectionsJson={
                        canDownloadExtractedSectionsJson
                          ? workflow.downloadExtractedSectionsJson
                          : null
                      }
                      onViewPdf={() => workflow.openUploadedPdf()}
                      onOpenLatestSaved={(sectionNumber) => {
                        setSectionReviewReturnTarget(null);
                        return workflow.openSectionFromHistory({
                          pdf: workflow.selectedPreviousPdf || workflow.dashboardPdf,
                          sectionNumber,
                        });
                      }}
                      onOpenSavedTestCases={({ sectionNumber, artifactVersion } = {}) => {
                        setSectionReviewReturnTarget(null);
                        const normalizedSection = Number(sectionNumber);
                        if (!Number.isFinite(normalizedSection) || normalizedSection <= 0) return;
                        workflow.openSectionFromHistory({
                          pdf: workflow.selectedPreviousPdf || workflow.dashboardPdf,
                          sectionNumber: normalizedSection,
                          targetStep: 5,
                        });
                        const targetPath = buildPdfWorkflowPath({
                          pdfId: workflow.pdfId,
                          sectionNumber: normalizedSection,
                          step: 5,
                          routeRoot,
                          applicationId: normalizedApplicationId,
                        });
                        const params = new URLSearchParams();
                        if (Number(artifactVersion) > 0) {
                          params.set('artifact_version', String(Number(artifactVersion)));
                        }
                        navigate(`${targetPath}${params.toString() ? `?${params.toString()}` : ''}`);
                      }}
                      onRegenerateDeprecatedSection={(sectionNumber) => {
                        setSectionReviewReturnTarget(null);
                        const normalizedSection = Number(sectionNumber);
                        if (!Number.isFinite(normalizedSection) || normalizedSection <= 0) return;
                        workflow.preAnalyzeSection({
                          replaceExisting: true,
                          sectionNumber: normalizedSection,
                        });
                      }}
                      onBrdChangeAnalysisUpdate={workflow.setBrdChangeAnalysis}
                      canRequestApproval={workflow.canRequestApproval}
                      canApprove={workflow.canApproveApproval}
                    />
                  )}

                  {workflow.currentStep === 3.5 && workflow.preAnalysisPlan && (
                    <TestCasePlanningStep
                      planData={workflow.preAnalysisPlan}
                      initialCounts={workflow.approvedCounts}
                      initialFocus={workflow.approvedFocus}
                      initialGlobalConstraints={workflow.approvedGlobalConstraints}
                      initialTestCaseFieldConfig={workflow.approvedTestCaseFieldConfig}
                      status={workflow.status}
                      showBackButton={false}
                      onBack={() => {
                        workflow.setConfirmedReplacementSection(null);
                        workflow.setCurrentStep(3);
                      }}
                      onGenerate={(planConfig) => {
                        workflow.setApprovedCounts({
                          positive_count: planConfig.positive_count,
                          negative_count: planConfig.negative_count,
                        });
                        workflow.setApprovedFocus(planConfig.additional_focus || []);
                        workflow.setApprovedGlobalConstraints(
                          planConfig.global_constraints || []
                        );
                        workflow.setApprovedTestCaseFieldConfig(
                          planConfig.test_case_field_config || null
                        );
                        return workflow.processSection(planConfig);
                      }}
                    />
                  )}

                  {workflow.currentStep === 4 && (workflow.bulkJob || ['processing', 'bulk_processing'].includes(workflow.status) || workflow.bulkLoading || !workflow.processingResult) && (
                    <GenerationProgressStep
                      sections={workflow.sections}
                      selectedSectionNumbers={workflow.bulkSelectedSections}
                      bulkJob={workflow.bulkJob}
                      bulkItems={workflow.bulkItems}
                      bulkEvents={workflow.bulkEvents}
                      bulkLoading={workflow.bulkLoading}
                      bulkError={workflow.bulkError}
                      onOpenOutput={(sectionNumber) => {
                        const returnBulkJobId = String(workflow.bulkJob?.id || parsedPathTarget.bulkJobId || '').trim();
                        if (returnBulkJobId) {
                          setSectionReviewReturnTarget({
                            type: 'bulk_generation',
                            pdfId: workflow.pdfId || parsedPathTarget.pdfId,
                            bulkJobId: returnBulkJobId,
                          });
                        }
                        return workflow.openSectionFromHistory({
                          pdf: workflow.selectedPreviousPdf || workflow.dashboardPdf,
                          sectionNumber,
                        });
                      }}
                    />
                  )}

                  {workflow.currentStep === 4 && workflow.processingResult && !workflow.bulkJob && !['processing', 'bulk_processing'].includes(workflow.status) && (
                    <>
                      <ResultsStep
                        processingResult={workflow.processingResult}
                        pdfId={workflow.pdfId}
                      />
                      <div className="mb-2 mt-12 flex justify-center">
                        <button
                          onClick={() => {
                            if (!hasAnyTestCases(workflow.editedTestCases)) {
                              workflow.setEditedTestCases(
                                getTestCasesForEditing(workflow.processingResult.test_cases)
                              );
                            }
                            workflow.setCurrentStep(5);
                          }}
                          className="primary-btn bg-gradient-to-r from-blue-600 to-cyan-600"
                        >
                          Review and Edit Test Cases
                        </button>
                      </div>
                    </>
                  )}

                  {workflow.currentStep === 5 && workflow.processingResult && (
                    <EditTestCasesStep
                      processingResult={workflow.processingResult}
                      pdfId={workflow.pdfId}
                      selectedSection={workflow.selectedSection}
                      currentUser={currentUser}
                      requestedArtifactVersion={Number.isFinite(routeArtifactVersion) && routeArtifactVersion > 0 ? routeArtifactVersion : null}
                      initialEditedTestCases={workflow.editedTestCases}
                      initialBaselineTestCases={workflow.originalTestCases}
                      status={workflow.status}
                      onSaveTestCases={workflow.handleSaveTestCases}
                      onEditComplete={workflow.handleEditTestCasesComplete}
                      onGenerateMore={workflow.isExcelSource ? null : handleGenerateMore}
                      sourceLabel={workflowSourceConfig.label}
                      enableManualImport={!workflow.isExcelSource}
                      showSourceBreakdown={!workflow.isExcelSource}
                      normalizationContext={{
                        context_type: workflowSourceConfig.contextType,
                        source_type: workflowSourceConfig.sourceType,
                        source_label: workflowSourceConfig.label,
                        pdf_id: workflow.pdfId,
                        section_number: workflow.processingResult?.section_number || workflow.selectedSection,
                        section_title: workflow.processingResult?.section_title || '',
                      }}
                    />
                  )}

                  {workflow.currentStep === 5.5 && workflow.processingResult && (
                    <GeneratePOMStep
                      pdfId={workflow.pdfId}
                      applicationId={normalizedApplicationId}
                      pomCatalogScope={applicationRouteMode ? 'application' : 'source'}
                      bulkJob={workflow.bulkJob}
                      bulkItems={workflow.bulkItems}
                      showBackButton={false}
                      onBack={() => workflow.setCurrentStep(6)}
                      onInternalBackChange={handlePomInternalBackChange}
                      onNext={handleContinueToAutomation}
                      onManageApplicationPoms={applicationRouteMode ? handleManageApplicationPoms : null}
                      onOpenApplicationPom={applicationRouteMode ? handleOpenApplicationPom : null}
                      continueToAutomationLoading={automationAuthChecking}
                    />
                  )}

                  {workflow.currentStep === 5.6 && workflow.processingResult && (
                    <MobilePOMStep
                      pdfId={workflow.pdfId}
                      editedTestCases={workflow.editedTestCases}
                      processingResult={workflow.processingResult}
                      testingType={workflow.testingType}
                      showBackButton={false}
                      onBack={() => workflow.setCurrentStep(6)}
                      onInternalBackChange={handlePomInternalBackChange}
                      onNext={() => workflow.setCurrentStep(7.5)}
                      onContinueAutomation={() => workflow.setCurrentStep(8)}
                    />
                  )}

                  {workflow.currentStep === 6 && (
                    <SaveTestsStep
                      testCases={workflow.editedTestCases}
                      baselineTestCases={workflow.originalTestCases}
                      pdfId={workflow.pdfId}
                      currentUserEmail={currentUser?.email || ''}
                      uploadedPdfName={
                        (workflow.isExcelSource
                          ? workflow.excelFile?.name || workflow.documentName
                          : workflow.file?.name)
                        || workflow.selectedPreviousPdf?.pdf_file
                        || null
                      }
                      apiResponse={workflow.apiResponse}
                      processingResult={workflow.processingResult}
                      testCaseApprovalStatus={workflow.testCaseApprovalStatus}
                      testCaseApprovalLoading={workflow.testCaseApprovalLoading}
                      canRequestTestCaseApproval={workflow.canRequestTestCaseApproval}
                      canApproveTestCaseApproval={workflow.canApproveTestCaseApproval}
                      canProceedToPOM={workflow.isTestCaseApprovedForCurrentSection}
                      isApiScriptFlow={workflow.isOpenApiSource}
                      testingTypeMode={workflow.isExcelSource ? 'web-only' : 'select'}
                      sourceLabel={workflowSourceConfig.label}
                      pomActionLabel={workflow.isExcelSource ? 'Prepare Web Automation' : 'Generate POM'}
                      showBackButton={false}
                      onRequestTestCaseApproval={workflow.requestTestCaseApproval}
                      onApproveTestCaseApproval={workflow.approveTestCaseApproval}
                      onRejectTestCaseApproval={workflow.rejectTestCaseApproval}
                      onBack={() => workflow.setCurrentStep(5)}
                      onGeneratePOM={(type) => {
                        if (workflow.isOpenApiSource || type === 'api') {
                          workflow.setTestingType('web');
                          workflow.setCurrentStep(7);
                          return;
                        }
                        if (workflow.isExcelSource) {
                          workflow.setTestingType('web');
                          workflow.setCurrentStep(5.5);
                          return;
                        }
                        // 'mobile' (local emulator) and 'browserstack' (cloud device)
                        // share the same Appium POM/script pipeline (step 5.6); only the
                        // run target differs. 'web' goes to the Playwright path (5.5).
                        const resolvedType = (type === 'mobile' || type === 'browserstack') ? type : 'web';
                        const isMobileFlow = resolvedType === 'mobile' || resolvedType === 'browserstack';
                        workflow.setTestingType(resolvedType);
                        workflow.setCurrentStep(isMobileFlow ? 5.6 : 5.5);
                      }}
                    />
                  )}

                  {workflow.currentStep === 7 && workflow.editedTestCases && (
                    <GenerateScriptsStep
                      editedTestCases={workflow.editedTestCases}
                      pdfId={workflow.pdfId}
                      applicationId={normalizedApplicationId}
                      automationAuthPdfId={automationAuthSourceId}
                      processingResult={workflow.processingResult}
                      prefilledScriptsByCaseId={workflow.historyScriptsByCaseId}
                      scriptFlow={workflow.isOpenApiSource ? 'api' : 'ui'}
                      theme={scriptStageTheme}
                      showBackButton={false}
                      onBack={() => workflow.setCurrentStep(workflow.isOpenApiSource ? 6 : 5.5)}
                      onComplete={() => workflow.setCurrentStep(8)}
                      onSaveTestCases={workflow.handleSaveTestCases}
                      onRunResultsChange={workflow.setRunResults}
                    />
                  )}

                  {workflow.currentStep === 7.5 && workflow.editedTestCases && (
                    // Mobile "Scripts" step — the Appium twin of the web GenerateScriptsStep
                    // (step 7). Reached from the mobile POM step's "Continue to Automation",
                    // mirroring web's POM -> Scripts -> Delivery step separation.
                    <MobileAutoTestStep
                      pdfId={workflow.pdfId}
                      editedTestCases={workflow.editedTestCases}
                      processingResult={workflow.processingResult}
                      testingType={workflow.testingType}
                      showBackButton={false}
                      onClose={() => workflow.setCurrentStep(5.6)}
                      onContinue={() => workflow.setCurrentStep(8)}
                    />
                  )}

                  {workflow.currentStep === 8 && workflow.editedTestCases && (
                    <ExecutionSummaryStep
                      editedTestCases={workflow.editedTestCases}
                      runResults={workflow.runResults}
                      pdfId={workflow.pdfId}
                      apiResponse={workflow.apiResponse}
                      processingResult={workflow.processingResult}
                      showBackButton={false}
                      onBack={() => workflow.setCurrentStep((workflow.testingType === 'mobile' || workflow.testingType === 'browserstack') ? 7.5 : 7)}
                      onExport={workflow.exportExecutionResults}
                    />
                  )}
        </WorkspaceStageCard>
      </WorkspacePage>

      {showAutomationLoginPrompt && (
        <div className="modal-overlay p-3 md:p-5">
          <div className="modal-shell max-w-lg overflow-hidden rounded-2xl bg-white">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                    <KeyRound className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Automation Login
                    </p>
                    <h2 className="text-lg font-bold text-slate-950">Does this product require login?</h2>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowAutomationLoginPrompt(false)}
                className="secondary-btn h-9 w-9 shrink-0 px-0 py-0"
                aria-label="Close login requirement prompt"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3 p-5">
              <button
                type="button"
                onClick={continueToAutomationScripts}
                className="flex w-full items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-left transition hover:bg-emerald-100"
              >
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
                <span>
                  <span className="block text-sm font-semibold text-slate-900">No login required</span>
                  <span className="block text-xs leading-5 text-slate-600">
                    Continue directly to generated script selection and execution.
                  </span>
                </span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowAutomationLoginPrompt(false);
                  setShowAutomationCaptureModal(true);
                }}
                className="flex w-full items-start gap-3 rounded-lg border border-blue-200 bg-white px-4 py-3 text-left transition hover:bg-blue-50"
              >
                <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
                <span>
                  <span className="block text-sm font-semibold text-slate-900">Requires login</span>
                  <span className="block text-xs leading-5 text-slate-600">
                    Capture a one-time browser session before opening automation.
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showAutomationCaptureModal && (
        <AutomationEnvironmentModal
          pdfId={automationAuthSourceId || workflow.pdfId}
          onClose={() => setShowAutomationCaptureModal(false)}
          onSaved={continueToAutomationScripts}
          onContinue={continueToAutomationScripts}
        />
      )}

      {!applicationRouteMode && workflow.showPreviousPDFs && (
        <PreviousPDFsModal
          historyMode={
            isApiTestingWorkflow
              ? 'openapi'
              : isTextRequirementsWorkflow
                ? 'requirements'
                : isExcelTestCasesWorkflow
                  ? 'testcase_excel'
                  : 'brd'
          }
          previousPDFs={workflow.previousPDFs}
          previousPDFsLoading={workflow.previousPDFsLoading}
          previousPDFsError={workflow.previousPDFsError}
          historyRestoreLoading={workflow.historyRestoreLoading}
          dashboardPdf={workflow.dashboardPdf}
          dashboardData={workflow.dashboardData}
          dashboardLoading={workflow.dashboardLoading}
          dashboardError={workflow.dashboardError}
          sharingStatus={workflow.brdSharingStatus}
          sharingRequests={workflow.brdSharingRequests}
          sharingLoading={workflow.brdSharingLoading}
          canRequestSharing={workflow.canRequestBrdSharing}
          canApproveSharing={workflow.canApproveBrdSharing}
          onPreview={workflow.previewPreviousPdf}
          onSelect={workflow.loadPreviousPdf}
          onDeletePdf={workflow.deletePreviousPdf}
          onOpenSection={async ({ pdf, sectionNumber, artifactVersion, targetStep = 5 } = {}) => {
            await workflow.openSectionFromHistory({ pdf, sectionNumber, targetStep });
            const targetPath = buildPdfWorkflowPath({
              pdfId: pdf?.pdf_id || workflow.pdfId,
              sectionNumber,
              step: targetStep,
              routeRoot,
              applicationId: normalizedApplicationId,
            });
            const params = new URLSearchParams();
            if (Number(artifactVersion) > 0) {
              params.set('artifact_version', String(Number(artifactVersion)));
            }
            navigate(`${targetPath}${params.toString() ? `?${params.toString()}` : ''}`);
          }}
          onRequestShare={workflow.requestBrdShare}
          onApproveShareRequest={workflow.approveBrdShareRequest}
          onRejectShareRequest={workflow.rejectBrdShareRequest}
          onRevokeShareAccess={workflow.revokeBrdShareAccess}
          onClose={() => workflow.setShowPreviousPDFs(false)}
        />
      )}

      {!applicationRouteMode && <WorkspaceMembersModal
        isOpen={workflow.showWorkspaceMembersModal}
        workspace={workflow.selectedWorkspace}
        members={workflow.workspaceMembers}
        loading={workflow.workspaceMembersLoading}
        error={workflow.workspaceMembersError}
        activeUsers={workflow.activeUsers}
        activeUsersLoading={workflow.activeUsersLoading}
        activeUsersError={workflow.activeUsersError}
        canManageMembers={workflow.canManageWorkspaceMembers}
        onClose={() => workflow.setShowWorkspaceMembersModal(false)}
        onRefresh={() => workflow.loadWorkspaceMembers()}
        onRefreshActiveUsers={workflow.loadActiveUsers}
        onAddMember={workflow.addWorkspaceMember}
        onUpdateRole={workflow.updateWorkspaceMemberRole}
        onRemoveMember={workflow.removeWorkspaceMember}
      />}
    </>
  );
};
